// Sagas for user experience interactions in dashboard
// (And for miscellaneous features that doesn't fit elsewhere)

import {
  fork,
  take,
  takeEvery,
  put,
  select,
  delay,
  call,
} from 'redux-saga/effects'
import { t } from 'ttag'

import { getUser } from 'ducks/auth'
import {
  INIT_SNACKS_QUEUE,
  SEND_MESSAGE,
  CREATE_MESSAGE,
  IMPORT_CANDIDATES,
  showModal,
  SHOW_MODAL,
  closeSnackbar,
  showSnackbar,
  getSnacks,
  initSnacksQueue,
} from 'ducks/ux'
import { errorAction } from 'ducks/error'
import { getLocation } from 'ducks/router'
import { candidates, templates } from 'orm'
import {
  DELETE_ERROR,
  DELETE_SUCCESS,
  POST_ERROR,
  POST_SUCCESS,
  PATCH_ERROR,
  PATCH_SUCCESS,
  TRASH,
} from 'orm/actionTypes'
import Api from 'utils/api'
import { renderMessage } from 'utils/messages'

export default function* uxSaga() {
  yield takeEvery(TRASH, trashConfirmSaga)
  yield takeEvery(SEND_MESSAGE, sendMessageSaga)
  yield takeEvery(IMPORT_CANDIDATES, importCandidatesSaga)
  yield takeEvery(CREATE_MESSAGE, createMessageSaga)
  yield takeEvery(INIT_SNACKS_QUEUE, initSnacksQueueSaga)
  yield takeEvery(SHOW_MODAL, refreshTemplates)
  yield takeEvery(SHOW_MODAL, clickWindow)
  yield takeEvery('*', routeChangeDeselectSaga)
  yield fork(snackbarListener)
}

function* clickWindow() {
  // Hacky way to close dropdown menus when a modal is shown
  yield call(() => document.documentElement.click())
}

function* refreshTemplates(action) {
  // Fetch project's templates when the send invite modal is opened
  if (action.payload?.modalType === 'SendInvite') {
    const project = action.payload.modalProps?.project
    yield put(templates.fetchStart({ project }))
  }
  // Fetch candidate data when the interview modal is opened
  if (action.payload?.modalType === 'PerformInterview') {
    const pk = action.payload.modalProps?.pk
    yield put(candidates.fetchStart({ pk }))
  }
}

function clearSelection() {
  // remove any user selected content from current page
  const selection = document.getSelection()
  if ((selection?.anchorNode as HTMLElement | null)?.role === 'search') return
  selection?.removeAllRanges()
}

function* routeChangeDeselectSaga(action) {
  // clear selected text or cursor on route change
  if (!/^router\//.test(action.type)) return
  yield call(clearSelection)
}

function* snackbarListener() {
  // listen for certain actions and emit snackbar messages that should be
  // displayed to the user.
  while (true) {
    const action = yield take('*')

    const payload = {
      [DELETE_ERROR]: { message: t`delete error`, type: 'error' },
      [DELETE_SUCCESS]: { message: t`delete success`, type: 'success' },
      [POST_ERROR]: { message: t`post error`, type: 'error' },
      [POST_SUCCESS]: { message: t`post success`, type: 'success' },
      [PATCH_ERROR]: { message: t`patch error`, type: 'error' },
      [PATCH_SUCCESS]: { message: t`saved`, type: 'success' },
    }[action.type]

    if (payload && action.meta.message) {
      payload.message = action.meta.message
    }

    if (!payload || action.meta.noSnackbar) continue
    yield put(initSnacksQueue(payload))
  }
}

function* autoHideSnackSaga(action) {
  const { id, timeout, type } = action.payload

  yield delay(timeout)
  yield put(closeSnackbar({ id, type }))
}

function* initSnacksQueueSaga(action) {
  const snacks = yield select(getSnacks)

  const { id, message, timeout, type } = action.payload

  if (snacks[type].length === 0) {
    yield put(showSnackbar({ id, message, timeout, type }))
    yield autoHideSnackSaga(action)

    return
  }

  const { id: snackId, type: snackType } = snacks[type][0]

  yield put(closeSnackbar({ id: snackId, type: snackType }))

  yield delay(200)
  yield put(showSnackbar({ id, message, timeout, type }))
  yield autoHideSnackSaga(action)
}

function* trashConfirmSaga(action) {
  const { pks } = action.payload
  const { model } = action.meta
  yield put(showModal('ConfirmDelete', { model, pks }))
}

function* importCandidatesSaga(action) {
  let { items, project } = action.payload
  // the url path determines the project
  if (!project) project = (yield select(getLocation))?.payload?.pk
  let ok = 0
  let fail = 0
  let duplicates = 0
  const cnn = Object.values<{ email?: string }>(
    yield select(candidates.getItems),
  )
  for (const item of items) {
    const exists = cnn.find(c => c?.email === item.email)
    if (exists) {
      duplicates++
      continue
    }
    const { response, error } = yield Api.post(['candidates'], {
      ...item,
      project,
    })
    if (response) ok++
    else {
      console.error(error)
      fail++
    }
  }
  if (duplicates)
    yield put(
      initSnacksQueue({
        message: t`${duplicates} candidates were not imported because they already exist.`,
        type: 'warning',
      }),
    )
  if (fail)
    yield put(
      initSnacksQueue({
        message: t`${fail} candidates could not be imported`,
        type: 'error',
      }),
    )
  if (ok) {
    yield put(
      initSnacksQueue({
        message: t`${ok} candidates imported`,
        type: 'success',
      }),
    )
    yield put(candidates.fetchStart({ project }))
  }
}

function* sendMessageSaga(action) {
  const { candidate, ...data } = action.payload
  const { error } = yield Api.post(['candidates', candidate.id, 'invite'], data)
  if (error) {
    yield put(errorAction(error))
  } else {
    yield put(candidates.fetchStart({ pk: candidate.id }))
    yield put(
      initSnacksQueue({
        message: t`${candidate.name} invited`,
        type: 'success',
      }),
    )
  }
}

function* createMessageSaga(action) {
  // render and send message
  const { to_candidate, message_type = 'invite.mail' } = action.payload
  const sender = yield select(getUser)
  const candidate = yield select(candidates.getItem(to_candidate))
  const templateSpec = {
    project: candidate.project,
    language: candidate.language,
    message_type,
  }
  const template = Object.values<Record<string, unknown>>(
    yield select(templates.getItems),
  ).find(item =>
    Object.entries(templateSpec).every(([key, value]) => item[key] === value),
  )
  if (!template) {
    console.error('template not found', templateSpec)
    yield put(
      initSnacksQueue({ message: t`Missing a template.`, type: 'error' }),
    )
    return
  }
  const mailData = renderMessage({
    template,
    candidate,
    sender,
  })
  const { error } = yield Api.post(
    ['candidates', to_candidate, 'invite'],
    mailData,
  )
  if (error) {
    console.error(error)
    yield put(errorAction(error))
  } else {
    yield put(candidates.fetchStart({ pk: to_candidate }))
    yield put(
      initSnacksQueue({
        message: t`${candidate.name} invited.`,
        type: 'success',
      }),
    )
  }
}
